import { Tab } from '@atlas/ui-primitives'
import { useState } from 'react'
import type { Key } from 'react-aria-components'
import { useCan } from '@/lib/capabilities'
import { notification } from '@/notifications/notification-store'

/**
 * The Explore tabs that ACT on the catalog record — Edit and Meta — are administrator
 * work: they change the brand or product row and the taxonomy value behind it, which
 * every other page classifies against.
 *
 * Shown-but-dimmed rather than hidden, which is the opposite of the rule the rest of the
 * app follows. A hidden control leaves a reader who expected it wondering whether they
 * are on the wrong page; here they are on the right page and simply lack the authority.
 * Selecting one is REFUSED rather than blocked, so the refusal can say who to ask — a
 * `isDisabled` tab takes no pointer events, and so can explain nothing at all.
 *
 * UX, never enforcement: the catalog writes behind these tabs are admin-gated server-side.
 */
const refusal = (subject: CatalogSubject): string =>
	`Editing this ${subject} and its metadata is an administrator task — ask an administrator if something here needs to change.`

export type CatalogSubject = 'brand' | 'product'

export interface CatalogAdminTabsState {
	/** Whether the signed-in role may use the Edit and Meta tabs. */
	canEdit: boolean
	selectedKey: Key
	onSelectionChange: (key: Key) => void
}

/**
 * Owns the tab strip's selection so a role that cannot edit is turned back at the tab
 * rather than at the save button, and told why at the moment it tries.
 */
export function useCatalogAdminTabs(
	defaultTab: string,
	adminTabs: string[],
	subject: CatalogSubject,
): CatalogAdminTabsState {
	const canEdit = useCan().canConfigure
	const [selectedKey, setSelectedKey] = useState<Key>(defaultTab)

	return {
		canEdit,
		selectedKey,
		onSelectionChange: (key) => {
			if (!canEdit && adminTabs.includes(String(key))) {
				notification.info(refusal(subject))
				return
			}
			setSelectedKey(key)
		},
	}
}

/**
 * The Edit and Meta tabs, in the strip's trailing position.
 *
 * Dimmed but still live. The explanation rides a `title` on the label rather than the
 * Tooltip primitive: a `TabList` builds a React Aria collection from its children, so a
 * tab cannot be wrapped in a trigger without dropping out of that collection, and a tab
 * forwards no `title` of its own to the DOM.
 */
export function CatalogAdminTabs({
	editTabId,
	metaTabId,
	canEdit,
	subject,
}: {
	editTabId: string
	metaTabId: string
	canEdit: boolean
	subject: CatalogSubject
}) {
	const unavailable = canEdit ? '' : 'cursor-not-allowed opacity-50'
	const why = canEdit ? undefined : refusal(subject)
	return (
		<>
			{/* Pushed right: these act on the record rather than reading its content. */}
			<Tab id={editTabId} className={`ml-auto ${unavailable}`}>
				<span title={why}>Edit</span>
			</Tab>
			<Tab id={metaTabId} className={unavailable}>
				<span title={why}>Meta</span>
			</Tab>
		</>
	)
}
